import { type ComponentProps, type ReactElement, type ReactNode } from 'react';
import { s, type SpacingToken } from '@/theme/tokens';
import { ChartEmptyState } from '@/ui/composites/charts/ChartEmptyState';
import { Panel } from '@/ui/composites/Panel';
import { Box, Surface } from '@/ui/primitives';
import {
  MetricsPageToolbar,
  type MetricsPageToolbarProps,
} from '../metricsToolbar/MetricsPageToolbar';
import { TABLE_PANEL_HEADER_HEIGHT } from '../tableSystem/tableConstants';
import { buildChartPanelSurfaceSx } from './chartPanelSurfaceSx';
type StatusChartPanelProps<TStatus extends string> = MetricsPageToolbarProps<TStatus> & {
  chartHeight?: number;
  width?: number;
  height?: number;
  headerOverride?: ReactNode;
  fixedHeightLayout?: boolean;
  fixedHeightPaddingBottom?: SpacingToken;
  panelPadding?: ComponentProps<typeof Panel>['padding'];
  panelContentGap?: ComponentProps<typeof Panel>['contentGap'];
  emptyTitle?: string;
  emptyBody?: string;
  children?: ReactNode;
};

export const StatusChartPanel = <TStatus extends string>({
  chartHeight = 400,
  width,
  height,
  headerOverride,
  fixedHeightLayout = false,
  fixedHeightPaddingBottom,
  panelPadding = 'xl',
  panelContentGap = 'md',
  emptyTitle = 'Status Chart Coming Soon',
  emptyBody = 'Use the toolbar to shape the chart data for the next phase of the UI update.',
  children,
  ...toolbarProps
}: StatusChartPanelProps<TStatus>): ReactElement => {
  const header = (
    <Box
      sx={{
        minHeight: `${toolbarProps.headerHeight ?? TABLE_PANEL_HEADER_HEIGHT}px`,
      }}
    >
      {headerOverride ?? <MetricsPageToolbar {...toolbarProps} />}
    </Box>
  );

  if (fixedHeightLayout && typeof height === 'number') {
    return (
      <Surface
        padding={panelPadding ?? 'xl'}
        sx={[
          ...buildChartPanelSurfaceSx({ width, height }),
          {
            display: 'flex',
            flexDirection: 'column',
            pb: fixedHeightPaddingBottom ? s(fixedHeightPaddingBottom) : undefined,
          },
        ]}
      >
        {header}
        <Box
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: s(panelContentGap),
          }}
        >
          {children ?? (
            <Box
              sx={{
                height: `${chartHeight}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ChartEmptyState title={emptyTitle} body={emptyBody} />
            </Box>
          )}
        </Box>
      </Surface>
    );
  }

  return (
    <Panel
      padding={panelPadding}
      gap="none"
      contentGap={panelContentGap}
      headerGap="none"
      headerSlot={header}
      sx={buildChartPanelSurfaceSx({ width, height })}
    >
      {children ?? (
        <Box
          sx={{
            height: `${chartHeight}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ChartEmptyState title={emptyTitle} body={emptyBody} />
        </Box>
      )}
    </Panel>
  );
};
